import React, { useCallback } from 'react';
import { useAsyncValue, useNavigate } from 'react-router-dom';
import { useRevalidateOnSync } from '../../../../offline/useRevalidateOnSync';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

// Components Styled
// OJO: locales a esta página, NO los del módulo RV — esta pantalla se monta
// dentro de ReportingLayout y tiene que dejar sitio al menú de Informes.
// Ver el comentario de `./styles`.
import { Container, ContainerButton } from './styles';

// Components
import { CheckBoxTagTable } from '../../../../components/display/tables/CheckBoxTagTable';
import { MainButton } from '../../../../components/inputs/buttons/MainButton';

// Types
import { tableIds } from '../../../../components/display/tables/types';
import { tableProps as CheckBoxTableProps } from '../../../../components/display/tables/CheckBoxTagTable/types';
import { SurveillanceRoutesLoaderData } from '../types';
import { OrderType } from '../../../../types/orders';

// Constants
import { SR_HIDDEN_COLUMN_IDS, SR_HIDDEN_FILTER_IDS } from '../constants';

// Utils
import { modalIds } from '../../../../components/modals/SuperModal/types';
import { openModal } from '../../../../state/actions/ui/modal';

// Hooks
import { usePersistentSelection } from '../../../../hooks/usePersistentSelection';
import { useSurveillanceRoutesListing } from '../../../../hooks/useSurveillanceRoutesListing';
import { getSurveillanceRouteFlagOrderId } from '../../../../utils/surveillanceRoute';

// Redux
import { getCursor } from '../../../../state/selectors/ui/tables';
import { store } from '../../../../state/configureStore';

export default function SearchAndCreateHistoricalRoutePage(): JSX.Element {
  const { t } = useTranslation(['pages', 'workOrders']);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const rawData = useAsyncValue() as SurveillanceRoutesLoaderData;
  useRevalidateOnSync();

  // Cursor Redux + polling + refetch — extraído a hook compartido con
  // SurveillanceRoutesPage (Task 2, refactor de patrones RV 2026-07-07).
  // Ver JSDoc de `useSurveillanceRoutesListing` para la única diferencia
  // real de comportamiento preservada entre ambos listados.
  const { adaptedData, onReFetchData, onRefetchPagination } =
    useSurveillanceRoutesListing(
      rawData,
      tableIds.SEARCH_AND_CREATE_HISTORICAL_SURVEILLANCE_ROUTES
    );

  // Persistent selection — all state managed by the hook
  const {
    rowSelection,
    onRowSelectionChange,
    selectedCount,
    selectAllMode,
    excludedIds,
    selectedRows,
    handleSelectAll,
    handleClearSelection,
  } = usePersistentSelection({
    dataAll: adaptedData.genericOrders || [],
    totalCount: adaptedData.totalCount || 0,
  });

  // El botón "Editar" se fue a la Vista general del módulo (2026-08-04), que
  // es donde está el de Accidentes y donde sí entra Tablet. Informes queda
  // como Actuaciones y Accidentes históricos: filtrar, ver y descargar.
  const handleViewRoute = useCallback(() => {
    const firstSelected = selectedRows[0];
    if (firstSelected) {
      navigate(`/surveillance-route/${firstSelected}`);
    }
  }, [selectedRows, navigate]);

  // Tour P2 (2026-07-06): el Informe PDF del RV vive DENTRO de
  // DownloadHistoricModal (botón "Informe", patrón actuaciones/accidentes,
  // spec §Informe PDF) — el handler v1 de descarga directa se movió allí.

  const handleDownloadHistory = useCallback(() => {
    const currentCursor = getCursor(
      tableIds.SEARCH_AND_CREATE_HISTORICAL_SURVEILLANCE_ROUTES
    )(store.getState());
    // GAP-5 paridad (2026-07-17): el cursor Redux del RV usa `routeName` pero
    // el input GraphQL FilterSurveillanceRouteList lo llama `name` — sin la
    // traducción, el spread `{...data.filters}` del modal (Excel/informe en
    // selectAll) mandaba `routeName` → error de coerción de variables y el
    // export caía al fallback DOM (o abortaba en silencio el combinado).
    const {
      id: _id,
      take: _take,
      cursor: _cursor,
      routeName: cursorRouteName,
      // Filtro Operarios (2026-07-12): `includeInactive` es SOLO-front (expande
      // las opciones del dropdown Operarios) — no existe en
      // FilterSurveillanceRouteList; si viajara en el spread `{...data.filters}`
      // del modal rompería la coerción de variables (mismo modo de fallo que
      // GAP-5 con routeName). `operatorIds` SÍ es campo backend y debe viajar.
      includeInactive: _includeInactive,
      ...filterFields
    } = currentCursor || ({} as any);
    if (cursorRouteName) {
      (filterFields as Record<string, unknown>).name = cursorRouteName;
    }
    dispatch(
      openModal(modalIds.DOWNLOAD_HISTORIC, {
        type: OrderType.SurveillanceRoute,
        filterPositionColumn: 2,
        selectedRows: selectAllMode ? [] : selectedRows,
        selectAll: selectAllMode,
        excludedIds: selectAllMode ? [...excludedIds] : [],
        filters: filterFields,
        totalCount: adaptedData.totalCount || 0,
        // Tour P2 (2026-07-06): Informe PDF DENTRO del modal, y COMBINADO
        // (patrón genéricas/campañas): el RV es un padre con hijas
        // (tramos + actuaciones asociadas) → portada + página del RV con la
        // tabla de tramos + una página por hija con fotos; ZIP si hay varios.
        showCombinedReport: true,
        entityType: 'surveillanceRoute',
      })
    );
  }, [selectedRows, dispatch, selectAllMode, excludedIds, adaptedData.totalCount]);

  return (
    <Container>
      <CheckBoxTagTable
        // `#historical-surveillanceRoute`: es el selector con el que
        // DownloadHistoricModal monta TableToExcel/TableToPDF. El export del RV
        // va por servidor, pero ese es el camino de respaldo cuando el
        // combinado falla (ver el comentario de GAP-5 en `handleDownloadHistory`)
        // y sin el id no había tabla que leer → fichero vacío sin error.
        idCss={'historical-' + OrderType.SurveillanceRoute}
        titleText={t('pages:surveillance.filterBy')}
        data={adaptedData as unknown as CheckBoxTableProps['data']}
        onReFetchData={onReFetchData}
        onReFetchPagination={onRefetchPagination}
        tableId={tableIds.SEARCH_AND_CREATE_HISTORICAL_SURVEILLANCE_ROUTES}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        selectAllMode={selectAllMode}
        excludedIds={excludedIds}
        onSelectAll={handleSelectAll}
        onClearSelection={handleClearSelection}
        selectedCount={selectedCount}
        // V4 FR-1.6 / FR-1.5: columnas y filtros no aplicables al dominio RV
        // (constantes compartidas con SurveillanceRoutesPage).
        hiddenColumnIds={SR_HIDDEN_COLUMN_IDS}
        hiddenFilterIds={SR_HIDDEN_FILTER_IDS}
        // V4 FR-1.6: añade columna "Nom del recorregut" tras ROID.
        showRouteNameColumn={true}
        // 2026-07-17: columna "Creado por" (Order gemela userId → nombre,
        // adapter creatorName). Solo RV.
        showCreatorColumn={true}
        // 2026-07-12 (feedback usuario): filtro Operarios como en Actuaciones
        // (multi-select RV.userIDs + toggle "incluir inactivos en Operarios").
        showOperatorsFilter={true}
        // V4 FR-1.7 (2026-05-27): doble-click → detalle SR. Idéntico
        // patrón que P1 (SurveillanceRoutesPage).
        onRowDoubleTap={(row) => {
          if (row.original.id) navigate(`/surveillance-route/${row.original.id}`);
        }}
        // Banderitas (spec 2026-10-22 § 8.4): columna y filtro «Solo marcadas».
        // La fila lleva en `id` el del recorrido; la bandera marca su Order.
        showFlagColumn
        showOnlyFlaggedFilter
        getFlagOrderId={getSurveillanceRouteFlagOrderId}
      />
      {/* V4 FR-2.5 + Tour P2 (2026-07-06): patrón canónico de históricos
          (órdenes/accidentes/actuaciones) — UN botón "Descargar el histórico"
          que abre DownloadHistoricModal con Histórico + Informe DENTRO del
          modal (spec §Informe PDF). "Nuevo recorrido" sigue eliminado (audit
          2026-05-27, FR-2.6: "el concepto crear històric no aplica aquí").
          "Editar" se fue a la Vista general del módulo (2026-08-04). */}
      <ContainerButton>
        <MainButton
          variant="secondary"
          text={t('pages:surveillance.seeRoute')}
          disabled={selectedCount !== 1}
          onClick={handleViewRoute}
        />
        <MainButton
          text={t('pages:workOrders.downloadHistoric')}
          disabled={selectedCount === 0}
          onClick={handleDownloadHistory}
        />
      </ContainerButton>
    </Container>
  );
}
